import { useContext, useEffect, useState } from 'react';
import { BookingConfig } from '../bookingSteps.interface';
import { BookingConfigContext } from '../../../context/bookingConfigContext/bookingConfigContext';
import { BookingContext } from '../../../context/bookingContext/bookingContext';
import { getBootstrapParamsFromUrl } from '../../../shared/helpers/appBootstrap.helpers';
import i18n, { getLanguage } from '../../../i18n';
import getUrlSearchParam from '../../../shared/helpers/urlSearchParams.helper';
import { AppConfigurationContext } from '../../../context/appConfigurationContext/appConfigurationContext';
import { CacheDataContext } from '../../../context/cacheContext/cacheContext';

interface FetchResult {
  bookingConfig: BookingConfig | null;
}

function delay(ms: number): Promise<unknown> {
  // eslint-disable-next-line no-promise-executor-return
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const useFetchBookingConfig = (
  setIsLoading: (loading: boolean) => void,
): FetchResult => {
  const [bookingConfig, setData] = useState<BookingConfig | null>(null);
  const { bookingSteps, update, updateDefault, setBookingReferenceId } =
    useContext(BookingContext);
  const { getUrlParam } = useContext(CacheDataContext);
  const { updateConfig } = useContext(BookingConfigContext);
  const { setAppError } = useContext(AppConfigurationContext);
  const brand: string | null = getUrlParam('brand');

  useEffect((): void => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const selectedBrand = brand ? brand.toLowerCase() : 'fcb_retail';
        const userLanguage = getLanguage();
        const timestamp = Date.now();
        const configUrl = `/config/config_booking_${selectedBrand}_${userLanguage}.json?timestamp=${timestamp}`;
        const response = await fetch(configUrl);
        if (!response.ok) {
          throw new Error(`Error fetching data: ${response.statusText}`);
        }
        const fetchedData = await response.json();
        // Fetch BW translations and override static fallback
        try {
          const translationsResponse = await fetch(
            `/config/translations/${userLanguage}.json?timestamp=${timestamp}`,
          );
          if (translationsResponse.ok) {
            const translations = await translationsResponse.json();
            i18n.addResourceBundle(
              userLanguage,
              'translation',
              translations,
              true,
              true,
            );
          }
        } catch {
          // Fallback to static en.json if translations fetch fails
        }
        updateDefault(fetchedData[0]);
        setData(fetchedData[0]);
        updateConfig(fetchedData[0]);
        const bootstrapBookingData = getBootstrapParamsFromUrl(
          fetchedData[0],
          bookingSteps,
          setBookingReferenceId,
        );
        if (bootstrapBookingData) {
          update(bootstrapBookingData, null!);
        }
      } catch (errorResponse) {
        if (errorResponse instanceof Error) {
          setAppError({
            error: errorResponse.message,
            description: 'Error fetching booking config',
            traceStack: errorResponse,
          });
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [setIsLoading]);

  return { bookingConfig };
};

export default useFetchBookingConfig;
